import React from 'react';
import ListItemText from '@mui/material/ListItemText';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { visuallyHidden } from '@mui/utils';
import { NavItem } from '../../config/navigationConfig.types';
import { NavTier1Button, NavTier2ListItem, NavTier3ListItem, NavText } from './navStyles';
import { useNavRouting } from './useNavRouting';
import { NAV_TEXT_COLOR, NAV_HOVER_BG_MAIN } from './navConstants';
import { navIconMap, defaultNavIcon } from './navIconMap';
import { NavIconImage } from './NavIconImage';
import { useApplicationContext } from '../../context/ApplicationContext';
import { useActiveNavItem } from '../../hooks/useActiveNavItem';

interface NavDirectLinkProps {
  item: NavItem;
  tier: 1 | 2 | 3;
  isNavOpen: boolean;
  onSelect?: () => void;
}

export const NavDirectLink: React.FC<NavDirectLinkProps> = ({ item, tier, isNavOpen, onSelect }) => {
  const { navigate } = useNavRouting();
  const { activeNavItemId, filteredNavigation } = useApplicationContext();
  const { getActiveItemId } = useActiveNavItem(filteredNavigation);

  // Get the active ID (either explicit or inferred)
  const currentActiveId = activeNavItemId ?? getActiveItemId();
  const isActive = currentActiveId === item.id;

  const handleClick = (e: React.MouseEvent) => {
    // Only remove focus for actual mouse clicks (e.detail > 0)
    // Keyboard activations (space/enter) have e.detail === 0 and should retain focus
    if (e.detail > 0) {
      (e.currentTarget as HTMLElement).blur();
    }
    onSelect?.();
    navigate(item);
  };

  // Tier 1: render as button with icon
  if (tier === 1) {
    const iconSrc = item.icon ? navIconMap[item.icon] ?? defaultNavIcon : defaultNavIcon;
    
    return (
      <NavTier1Button 
        onClick={handleClick}
        aria-current={isActive ? 'page' : undefined}
        sx={{
          backgroundColor: isActive ? NAV_HOVER_BG_MAIN : 'transparent',
          fontWeight: isActive ? 600 : 400,
        }}
      >
        {item.icon && <NavIconImage src={iconSrc} />}
        <NavText
          tier={1}
          sx={{
            opacity: isNavOpen ? 1 : 0,
            transition: 'opacity 200ms',
            fontWeight: isActive && tier !== 1 ? 600 : 400,
            flexGrow: 1,
            minWidth: 0,
            textAlign: 'left',
          }}
        >
          {item.label}
        </NavText>
        {item.isExternal && (
          <>
            <OpenInNewOutlinedIcon sx={{ fontSize: 16, color: NAV_TEXT_COLOR, flexShrink: 0, mr: '3px' }} />
            <span style={visuallyHidden}>(opens in new window)</span>
          </>
        )}
      </NavTier1Button>
    );
  }

  // Tier 2 and 3: render as list item
  const ListItem = tier === 2 ? NavTier2ListItem : NavTier3ListItem;

  return (
    <ListItem
      onClick={handleClick}
      aria-current={isActive ? 'page' : undefined}
      sx={{
        display: isNavOpen ? 'flex' : 'none',
        '& .MuiListItemText-primary': {
          fontWeight: isActive ? 600 : 400,
          textDecoration: isActive ? 'underline' : 'none',
        },
      }}
    >
      <ListItemText primary={item.label} />
      {item.isExternal && (
        <>
          <OpenInNewOutlinedIcon sx={{ fontSize: 16, color: NAV_TEXT_COLOR, ml: 0.5, mr: '2px' }} />
          <span style={visuallyHidden}>(opens in new window)</span>
        </>
      )}
    </ListItem>
  );
};
